import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlug, faPlay, faStop, faPaperPlane, faSpinner,
  faArrowUp, faArrowDown, faSync, faTrash, faBolt, faCaretDown
} from '@fortawesome/free-solid-svg-icons';
import webSocketService from '../../services/webSocketServiceFrontend';
import authProfileService from '../../services/authProfileService';
import axios from '../../config/axiosConfig';

const WebSocketTab = ({ webTarget, isDarkMode, proxySessionId }) => {
  const [connections, setConnections] = useState([]);
  const [selectedConn, setSelectedConn] = useState(null);
  const [messages, setMessages] = useState([]);
  const [authProfiles, setAuthProfiles] = useState([]);
  const [connectForm, setConnectForm] = useState({ url: '', authProfileId: '', protocols: '' });
  const [messageInput, setMessageInput] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [sending, setSending] = useState(false);
  const [detectedEndpoints, setDetectedEndpoints] = useState([]);
  const [showWsDropdown, setShowWsDropdown] = useState(false);
  const messagesEndRef = useRef(null);
  const wsDropdownRef = useRef(null);

  useEffect(() => {
    if (webTarget?._id) loadConnections();
    if (webTarget?.project) authProfileService.getAll(webTarget.project).then(setAuthProfiles).catch(() => {});
    if (proxySessionId) loadDetectedWebSockets();
  }, [webTarget, proxySessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wsDropdownRef.current && !wsDropdownRef.current.contains(event.target)) {
        setShowWsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadConnections = async () => {
    try {
      const data = await webSocketService.getConnections(webTarget._id);
      setConnections(data);
    } catch {}
  };

  const loadDetectedWebSockets = async () => {
    if (!proxySessionId) return;
    try {
      const res = await axios.get(`/proxy-sessions/${proxySessionId}/detected-websockets`);
      setDetectedEndpoints(res.data || []);
    } catch {}
  };

  const loadMessages = async (connectionId) => {
    try {
      const data = await webSocketService.getMessages(connectionId);
      setMessages(data);
    } catch {}
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const conn = await webSocketService.connect({
        webTargetId: webTarget._id,
        projectId: webTarget.project,
        url: connectForm.url,
        authProfileId: connectForm.authProfileId || undefined,
        protocols: connectForm.protocols ? connectForm.protocols.split(',').map(s => s.trim()) : undefined
      });
      setSelectedConn(conn);
      loadConnections();
      setConnectForm({ url: '', authProfileId: '', protocols: '' });
    } catch (err) {
      console.error('WebSocket connect error:', err);
    } finally {
      setConnecting(false);
    }
  };

  const handleSend = async () => {
    if (!selectedConn || !messageInput) return;
    setSending(true);
    try {
      await webSocketService.send(selectedConn._id || selectedConn.connectionId, {
        data: messageInput,
        opcode: 1
      });
      setMessageInput('');
      loadMessages(selectedConn._id || selectedConn.connectionId);
    } catch {} finally {
      setSending(false);
    }
  };

  const handleClose = async (connId) => {
    try {
      await webSocketService.close(connId);
      if (selectedConn?._id === connId) setSelectedConn(null);
      loadConnections();
    } catch {}
  };

  const handleReplay = async (messageId) => {
    try {
      await webSocketService.replay(messageId, {
        connectionId: selectedConn._id || selectedConn.connectionId
      });
      loadMessages(selectedConn._id || selectedConn.connectionId);
    } catch {}
  };

  const inputClass = `text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`flex items-center justify-between px-3 py-2 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faPlug} className="text-cyan-400" />
          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            WebSocket Testing - {connections.length} connections
          </span>
        </div>
      </div>

      {/* Connect form */}
      <div className={`px-3 py-2 border-b flex items-center gap-2 ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50/50'}`}>
        <div className="flex-1 flex items-center gap-1 relative">
          <input
            value={connectForm.url}
            onChange={e => setConnectForm(p => ({ ...p, url: e.target.value }))}
            placeholder="wss://example.com/ws or ws://localhost:8080/ws"
            className={`flex-1 ${inputClass}`}
          />
          {detectedEndpoints.length > 0 && (
            <div className="relative" ref={wsDropdownRef}>
              <button
                onClick={() => setShowWsDropdown(!showWsDropdown)}
                className={`px-2 py-1.5 rounded border text-xs flex items-center gap-1 ${
                  isDarkMode
                    ? 'bg-gray-900 border-gray-600 text-cyan-400 hover:bg-gray-700'
                    : 'bg-white border-gray-300 text-cyan-600 hover:bg-gray-50'
                }`}
                title={`${detectedEndpoints.length} detected WebSocket${detectedEndpoints.length > 1 ? 's' : ''}`}
              >
                <FontAwesomeIcon icon={faBolt} className="text-[10px]" />
                <span>{detectedEndpoints.length}</span>
                <FontAwesomeIcon icon={faCaretDown} className="text-[8px]" />
              </button>
              {showWsDropdown && (
                <div className={`absolute right-0 top-full mt-1 w-96 max-h-64 overflow-y-auto rounded border shadow-lg z-50 ${
                  isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
                }`}>
                  <div className={`px-2 py-1.5 border-b text-[10px] font-medium ${
                    isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'
                  }`}>
                    <FontAwesomeIcon icon={faBolt} className="text-yellow-500" /> Detected WebSockets
                  </div>
                  {detectedEndpoints.map((ws, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setConnectForm(p => ({ ...p, url: ws.url, protocols: ws.protocols || '' }));
                        setShowWsDropdown(false);
                      }}
                      className={`w-full px-2 py-2 text-left border-b text-xs font-mono hover:bg-opacity-50 ${
                        isDarkMode
                          ? 'border-gray-700 hover:bg-cyan-900/20'
                          : 'border-gray-100 hover:bg-cyan-50'
                      }`}
                    >
                      <div className={`truncate ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                        {ws.url}
                      </div>
                      {ws.protocols && (
                        <div className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          Protocol: {ws.protocols}
                        </div>
                      )}
                      {ws.detectedAt && (
                        <div className={`text-[9px] mt-0.5 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                          Detected: {new Date(ws.detectedAt).toLocaleString()}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <select value={connectForm.authProfileId}
          onChange={e => setConnectForm(p => ({ ...p, authProfileId: e.target.value }))}
          className={`w-36 ${inputClass}`}>
          <option value="">No Auth</option>
          {authProfiles.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
        <button onClick={handleConnect} disabled={connecting || !connectForm.url}
          className="px-3 py-1.5 bg-cyan-600 text-white rounded text-xs hover:bg-cyan-700 disabled:opacity-50 flex items-center gap-1">
          {connecting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlay} />} Connect
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Connection list */}
        <div className={`w-56 border-r overflow-y-auto ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {connections.map(conn => (
            <div key={conn._id}
              onClick={() => { setSelectedConn(conn); loadMessages(conn._id); }}
              className={`px-3 py-2 cursor-pointer border-b ${
                selectedConn?._id === conn._id
                  ? (isDarkMode ? 'bg-cyan-900/20 border-gray-700' : 'bg-cyan-50 border-gray-200')
                  : (isDarkMode ? 'border-gray-800 hover:bg-gray-800' : 'border-gray-100 hover:bg-gray-50')
              }`}>
              <div className="flex items-center justify-between">
                <span className={`text-[10px] font-mono truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {conn.url ? new URL(conn.url).pathname : conn._id.slice(-6)}
                </span>
                <span className={`w-2 h-2 rounded-full ${conn.status === 'open' ? 'bg-green-400' : 'bg-gray-500'}`} />
              </div>
              <div className="flex items-center gap-2 mt-1">
                {conn.status === 'open' && (
                  <button onClick={(e) => { e.stopPropagation(); handleClose(conn._id); }}
                    className="text-[10px] text-red-400 hover:text-red-300">
                    <FontAwesomeIcon icon={faStop} /> Close
                  </button>
                )}
                <span className={`text-[10px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                  {conn.messageCount || 0} msgs
                </span>
              </div>
            </div>
          ))}
          {connections.length === 0 && (
            <div className={`p-4 text-xs text-center ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              No connections. Enter a WebSocket URL above.
            </div>
          )}
        </div>

        {/* Messages panel */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {selectedConn ? (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-1">
                {messages.map((msg, i) => (
                  <div key={msg._id || i} className={`flex items-start gap-2 ${msg.direction === 'sent' ? 'justify-end' : ''}`}>
                    <div className={`max-w-[80%] px-3 py-1.5 rounded-lg text-xs font-mono ${
                      msg.direction === 'sent'
                        ? (isDarkMode ? 'bg-cyan-900/30 text-cyan-200' : 'bg-cyan-100 text-cyan-800')
                        : (isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700')
                    }`}>
                      <div className="flex items-center gap-2 mb-0.5">
                        <FontAwesomeIcon
                          icon={msg.direction === 'sent' ? faArrowUp : faArrowDown}
                          className={`text-[8px] ${msg.direction === 'sent' ? 'text-cyan-400' : 'text-green-400'}`}
                        />
                        <span className={`text-[9px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                          {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}
                        </span>
                        <button onClick={() => handleReplay(msg._id)}
                          className={`text-[9px] ${isDarkMode ? 'text-gray-600 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'}`}>
                          <FontAwesomeIcon icon={faSync} /> Replay
                        </button>
                      </div>
                      <div className="whitespace-pre-wrap break-all">{msg.data}</div>
                      {msg.length && (
                        <div className={`text-[9px] mt-0.5 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                          {msg.length} bytes
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
                {messages.length === 0 && (
                  <div className={`text-center py-8 text-xs ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                    No messages yet. Send a message or wait for incoming data.
                  </div>
                )}
              </div>

              {/* Send input */}
              <div className={`px-3 py-2 border-t flex gap-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <input
                  value={messageInput}
                  onChange={e => setMessageInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSend()}
                  placeholder="Type a message..."
                  className={`flex-1 ${inputClass}`}
                />
                <button onClick={handleSend} disabled={sending || !messageInput}
                  className="px-3 py-1.5 bg-cyan-600 text-white rounded text-xs hover:bg-cyan-700 disabled:opacity-50">
                  {sending ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPaperPlane} />}
                </button>
              </div>
            </>
          ) : (
            <div className={`flex items-center justify-center h-full text-sm ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              Select a connection or create a new one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebSocketTab;
